import React, { useState, useCallback } from 'react';
import { NumberBaseConverter as NumberBaseConverterUtil, NumberOutput } from '../../lib/formatters/numberBaseConverter';
import './NumberBaseConverter.css';

interface NumberBaseConverterProps {
  initialValue?: string;
}

interface Notification {
  type: 'success' | 'error' | 'info';
  message: string;
}

export const NumberBaseConverter: React.FC<NumberBaseConverterProps> = ({ initialValue = '' }) => {
  const [input, setInput] = useState(initialValue);
  const [inputBase, setInputBase] = useState<'binary' | 'octal' | 'decimal' | 'hexadecimal'>('decimal');
  const [result, setResult] = useState<NumberOutput | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  const converter = new NumberBaseConverterUtil();

  const showNotification = (type: Notification['type'], message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  const handleBaseChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setInputBase(e.target.value as 'binary' | 'octal' | 'decimal' | 'hexadecimal');
  }, []);

  const handleConvert = useCallback(() => {
    if (!input.trim()) {
      showNotification('error', 'Please enter a number to convert');
      return;
    }

    try {
      const conversionResult = converter.convert(input, inputBase);
      setResult(conversionResult);

      if (conversionResult.isValid) {
        showNotification('success', 'Successfully converted number');
      } else {
        showNotification('error', conversionResult.error || 'Invalid input');
      }
    } catch (error) {
      showNotification('error', error instanceof Error ? error.message : 'An error occurred');
    }
  }, [input, inputBase, converter]);

  const handleCopyToClipboard = useCallback(async (value: string | null, label: string) => {
    if (!value) {
      showNotification('error', 'No value to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopiedValue(label);
      setTimeout(() => setCopiedValue(null), 2000);
      showNotification('success', `Copied ${label} to clipboard`);
    } catch (error) {
      showNotification('error', 'Failed to copy to clipboard');
    }
  }, []);

  const handleClear = useCallback(() => {
    setInput('');
    setResult(null);
    setNotification(null);
  }, []);

  const handleSample = useCallback(() => {
    setInput('255');
    setInputBase('decimal');
  }, []);

  const supportedBases = converter.getSupportedBases();

  return (
    <div className="number-base-converter" role="main" aria-label="Number Base Converter">
      <h1>Number Base Converter</h1>
      <p>Convert numbers between binary, octal, decimal, and hexadecimal formats.</p>

      <div className="converter-section">
        <section aria-labelledby="input-section">
          <h2 id="input-section" className="section-title">Input</h2>

          <div className="input-controls">
            <div className="input-group">
              <label htmlFor="number-input" className="input-label">
                Enter Number:
              </label>
              <input
                id="number-input"
                type="text"
                value={input}
                onChange={handleInputChange}
                placeholder="Enter a number..."
                className="number-input"
                aria-label="Number input"
                spellCheck={false}
              />
            </div>

            <div className="input-group">
              <label htmlFor="base-select" className="input-label">
                Input Base:
              </label>
              <select
                id="base-select"
                value={inputBase}
                onChange={handleBaseChange}
                className="base-select"
                aria-label="Select input base"
              >
                {supportedBases.map(base => (
                  <option key={base.value} value={base.value}>
                    {base.label} - {base.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="action-buttons" role="toolbar" aria-label="Conversion actions">
            <button
              onClick={handleConvert}
              className="btn btn-primary"
              aria-label="Convert number"
            >
              Convert
            </button>
            <button
              onClick={handleClear}
              className="btn btn-secondary"
              aria-label="Clear input and results"
            >
              Clear
            </button>
            <button
              onClick={handleSample}
              className="btn btn-secondary"
              aria-label="Load sample number"
            >
              Sample (255)
            </button>
          </div>
        </section>

        {notification && (
          <div
            className={`notification notification-${notification.type}`}
            role="alert"
            aria-live="polite"
          >
            {notification.message}
          </div>
        )}

        {result && (
          <section aria-labelledby="results-section">
            <h2 id="results-section" className="section-title">Results</h2>

            <div className="results-grid">
              <div className="result-item">
                <div className="result-header">
                  <h3>Decimal</h3>
                  <button
                    onClick={() => handleCopyToClipboard(result.decimalValue?.toString() || null, 'decimal')}
                    className="copy-btn"
                    aria-label="Copy decimal value"
                    disabled={result.decimalValue === null}
                  >
                    {copiedValue === 'decimal' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="result-value" aria-live="polite">
                  {result.decimalValue !== null ? result.decimalValue : 'N/A'}
                </div>
              </div>

              <div className="result-item">
                <div className="result-header">
                  <h3>Binary</h3>
                  <button
                    onClick={() => handleCopyToClipboard(result.binaryValue, 'binary')}
                    className="copy-btn"
                    aria-label="Copy binary value"
                    disabled={result.binaryValue === null}
                  >
                    {copiedValue === 'binary' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="result-value" aria-live="polite">
                  {result.binaryValue !== null ? result.binaryValue : 'N/A'}
                </div>
              </div>

              <div className="result-item">
                <div className="result-header">
                  <h3>Octal</h3>
                  <button
                    onClick={() => handleCopyToClipboard(result.octalValue, 'octal')}
                    className="copy-btn"
                    aria-label="Copy octal value"
                    disabled={result.octalValue === null}
                  >
                    {copiedValue === 'octal' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="result-value" aria-live="polite">
                  {result.octalValue !== null ? result.octalValue : 'N/A'}
                </div>
              </div>

              <div className="result-item">
                <div className="result-header">
                  <h3>Hexadecimal</h3>
                  <button
                    onClick={() => handleCopyToClipboard(result.hexadecimalValue, 'hexadecimal')}
                    className="copy-btn"
                    aria-label="Copy hexadecimal value"
                    disabled={result.hexadecimalValue === null}
                  >
                    {copiedValue === 'hexadecimal' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="result-value" aria-live="polite">
                  {result.hexadecimalValue !== null ? result.hexadecimalValue : 'N/A'}
                </div>
              </div>
            </div>

            {!result.isValid && result.error && (
              <div className="error-details">
                <h4>Error Details:</h4>
                <p>{result.error}</p>
              </div>
            )}
          </section>
        )}

        {result === null && input && (
          <div className="empty-state" role="status" aria-live="polite">
            <p>Enter a number and click "Convert" to see results.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberBaseConverter;